import { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import GlobalContext from "../context/GlobalContext.js";
import { CheckCircle2, XCircle } from 'lucide-react';

export default function SsoCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { settings } = useContext(GlobalContext);
    const primaryColor = settings?.primary_hex_colour || '#813468';

    const [status, setStatus] = useState('processing');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        handleCallback();
    }, []);

    async function handleCallback() {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
            setStatus('error');
            setErrorMessage(getErrorMessage(error, errorDescription));
            return;
        }

        if (!code || !state) {
            setStatus('error');
            setErrorMessage('Missing authorization code or state from Microsoft.');
            return;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/sso/callback`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ code, state })
                }
            );

            const result = await response.json();

            if (response.ok) {
                setStatus('success');
                localStorage.setItem('apiToken', result.token);
                localStorage.setItem('role', result.role);
                sessionStorage.removeItem('sso_email');
                setTimeout(() => {
                    window.location.href = result.role === 'admin' ? '/admin' : '/';
                }, 1000);
            } else {
                setStatus('error');
                setErrorMessage(result.message || 'SSO login failed.');
            }
        } catch {
            setStatus('error');
            setErrorMessage('An error occurred during authentication. Please try again.');
        }
    }

    function getErrorMessage(error, description) {
        switch (error) {
            case 'access_denied':
                return 'Access was denied. You may have cancelled the login or lack permission.';
            case 'login_required':
                return 'Login is required. Please try again.';
            case 'consent_required':
                return 'Admin consent is required for this application.';
            case 'interaction_required':
                return 'Additional interaction is required. Please try again.';
            case 'server_error':
                return 'Microsoft encountered an error. Please try again later.';
            case 'temporarily_unavailable':
                return 'Microsoft login is temporarily unavailable. Please try again later.';
            default:
                return description || 'Authentication failed. Please try again.';
        }
    }

    return (
        <div className="min-h-screen flex flex-col items-center justify-center px-6 py-12" style={{ backgroundColor: '#F2F2F7' }}>
            <div className="w-full max-w-sm mx-auto text-center">

                {status === 'processing' && (
                    <>
                        <div className="w-12 h-12 mx-auto mb-4 rounded-full animate-spin" style={{ borderWidth: '3px', borderColor: `${primaryColor}30`, borderTopColor: primaryColor, borderStyle: 'solid' }} />
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Signing you in</h2>
                        <p className="text-sm text-gray-500">Please wait while we verify your identity...</p>
                    </>
                )}

                {status === 'success' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 size={32} className="text-green-600" strokeWidth={1.5} />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">You're in</h2>
                        <p className="text-sm text-gray-500">Redirecting you now...</p>
                    </>
                )}

                {status === 'error' && (
                    <>
                        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                            <XCircle size={32} className="text-red-500" strokeWidth={1.5} />
                        </div>
                        <h2 className="text-xl font-bold text-gray-900 mb-2">Authentication failed</h2>
                        <p className="text-sm text-red-500 mb-8">{errorMessage}</p>
                        <button
                            onClick={() => navigate('/login')}
                            className="w-full flex items-center justify-center rounded-2xl py-4 text-base font-semibold text-white transition-opacity hover:opacity-90"
                            style={{ backgroundColor: primaryColor }}
                        >
                            Back to Sign In
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
